import {
    getDefaultHeaders,
    getHeaders,
    getPieceHeaders,
    getInvoiceHeaders,
    getDefaultPieceHeaders,
    getDefaultInvoiceHeaders,
    getOrganisationMandatoryHeaders,
} from 'network/settings-product';
import * as React from 'react';
import { StylesProps } from 'theme/jss-types';
import { Header, TableColumn } from './settings.types';
import {
    Button,
    message,
} from 'antd';
import { headerColumns } from './settings.constants';
import { accountStyles } from './settings.styles';
import ResetHeaders from './reset-headers';
import EditHeaders from './edit-headers';
import { HocOptions } from '../common/generic-hoc.types';
import { ReduxStore } from '../../reducers/redux.types';
import { MasterConfig } from 'types/master-data-types';
import GenericHoc from '../common/generic-hoc';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { EditOutlined } from '@ant-design/icons';
import { useMemo } from 'react';

interface HeaderMappingProps extends StylesProps<ReturnType<typeof accountStyles>> {
    config: MasterConfig;
    headerType?: string;
}

const HeaderMapping = (props: HeaderMappingProps) => {
    const {
        classes,
        config,
        headerType,
    } = props;
    const { t } = useTranslation();

    const freezeDefaultMandatoryHeaders = config?.customer_portal_config?.allow_freeze_default_mandatory_headers || false;

    const [headers, setHeaders] = React.useState<Header[]>([]);
    const [defaultHeaders, setDefaultHeaders] = React.useState<Header[]>([]);
    const [orgMandatoryHeaders, setOrgMandatoryHeaders] = React.useState<Header[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [toggle, setToggle] = React.useState<boolean>(true);
    const [resetVisible, setResetVisible] = React.useState<boolean>(false);
    const [editVisible, setEditVisible] = React.useState<boolean>(false);
    const configuredHeaders = useMemo(() => {
        return headers?.filter((header: Header) => header.positionAlpha !== 'Not Present')
            ?.sort((a, b) => {
                const aLen = a.positionAlpha.length;
                const bLen = b.positionAlpha.length;
                if (aLen !== bLen) {
                    return aLen - bLen;
                }
                return a.positionAlpha.localeCompare(b.positionAlpha);
            });
    }, [headers]);

    const loadHeaders = async () => {
        setLoading(true);
        let response;
        switch (headerType) {
            case 'piece':
                response = await getPieceHeaders({ searchKey: '' });
                break;
            case 'invoice':
                response = await getInvoiceHeaders({ searchKey: '' });
                break;
            default:
                response = await getHeaders({ searchKey: '' });
        }

        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setHeaders(response?.data || {});
        }
        setLoading(false);
    };

    const loadDefaults = async () => {
        let response;
        switch (headerType) {
            case 'piece':
                response = await getDefaultPieceHeaders({});
                break;
            case 'invoice':
                response = await getDefaultInvoiceHeaders({});
                break;
            default:
                response = await getDefaultHeaders({});
        }
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setDefaultHeaders(response?.data || {});
        }
    };

    const loadOrganisationMandatoryHeaders = async () => {
        const response = await getOrganisationMandatoryHeaders();
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setOrgMandatoryHeaders(response?.data || {});
        }
    };

    React.useEffect(() => {
        loadHeaders();
        loadDefaults();
        if (freezeDefaultMandatoryHeaders) {
            loadOrganisationMandatoryHeaders();
        }
    }, [toggle]);

    const renderTitle = () => {
        switch (headerType) {
            case 'piece': return t('piece_header_name_mapping');
            case 'invoice': return t('invoice_header_name_mapping');
            default: return t('header_name_mapping');
        }
    };

    const renderTitleBar = () => {
        return (
            <div
                className={classes.title}
                style={{ borderBottom: 'none' }}
            >
                {renderTitle()}
                <div className={classes.buttons}>
                    <Button
                        type="primary"
                        disabled={loading}
                        onClick={() => setResetVisible(true)}
                        className={classes.newButton}
                    >
                        {t('reset').toUpperCase()}
                    </Button>
                    <Button
                        type="primary"
                        disabled={loading}
                        onClick={() => setEditVisible((prev) => !prev)}
                        className={classes.newButton}
                        icon={<EditOutlined />}
                    >
                        {t('edit').toUpperCase()}
                    </Button>
                </div>
            </div>
        );
    };

    const renderText = (text: string | number) => {
        return (
            <div className={classes.cellValue}>
                {text}
            </div>
        );
    };

    const renderColumn = (
        text: string,
        row: Header,
        column: string,
        index: number,
    ) => {
        switch (column) {
            case 'index': return renderText(index + 1);
            case 'name': return renderText(text);
            case 'mandatory': return renderText(`${row?.mandatory ? 'YES' : '-'}`);
            default: return renderText(text as string);
        }
    };

    const renderColumns = () => {
        const columns: any = headerColumns.map((column: TableColumn) => {
            return {
                key: column.key,
                title: column.key === 'name' ? t('header_name').toUpperCase() : t(column.key).toUpperCase(),
                dataIndex: column.key,
                ellipsis: true,
                bordered: false,
                render: (text: string, row: any, idx: number) => renderColumn(text, row, column.key, idx),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                columns={renderColumns()}
                loading={loading}
                dataSource={configuredHeaders}
                pagination={false}
                rowKey={(item: Header) => (item.id)}
                scroll={{ x: false }}
                style={{
                    flexGrow: 1,
                    background: '#ffffff',
                }}
            />
        );
    };

    const handleResetClose = (reload?: boolean) => {
        setResetVisible(false);
        if (reload) {
            setToggle(!toggle);
        }
    };

    const renderReset = () => {
        if (!resetVisible) {
            return null;
        }
        return (
            <ResetHeaders
                onClose={(reload) => handleResetClose(reload)}
                headers={defaultHeaders}
                headerType={headerType}
            />
        );
    };

    const handleEditClose = (reload?: boolean) => {
        setEditVisible((prev) => !prev);
        if (reload) {
            setToggle(!toggle);
        }
    };

    const renderEdit = () => {
        if (!headers || !headers?.length || !editVisible) {
            return null;
        }
        return (
            <EditHeaders
                editVisible={editVisible}
                onClose={(reload: boolean) => handleEditClose(reload)}
                headers={headers}
                orgMandatoryHeaders={orgMandatoryHeaders}
                freezeDefaultMandatoryHeaders={freezeDefaultMandatoryHeaders}
                headerType={headerType}
            />
        );
    };

    return (
        <div className={classes.main}>
            {renderTitleBar()}
            {renderTable()}
            {renderReset()}
            {renderEdit()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    return {
        config: state.master.config,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: accountStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const HeaderMappingPage = GenericHoc(hocConfig)(HeaderMapping);

export default HeaderMappingPage;
